import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface TeamAdmin {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  isRemovable: boolean; // True if admin can be removed from this team (it's their top-level team)
  teamsadminnest?: any; // The admin's team hierarchy from their userdetails
}

interface TeamTrainee {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  role?: string; // Direct role on team
  derived?: { [subTeamId: string]: { role: string } }; // Role from sub-teams
}

interface AvailableUser {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
}

interface TeamDataContextType {
  admins: TeamAdmin[];
  candidates: TeamTrainee[];
  availableUsers: AvailableUser[];
  isLoading: boolean;
  setAdmins: (admins: TeamAdmin[]) => void;
  setCandidates: (candidates: TeamTrainee[]) => void;
  setAvailableUsers: (users: AvailableUser[]) => void;
  setIsLoading: (loading: boolean) => void;
  clearData: () => void;
}

const TeamDataContext = createContext<TeamDataContextType | undefined>(undefined);

export const TeamDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admins, setAdmins] = useState<TeamAdmin[]>([]);
  const [candidates, setCandidates] = useState<TeamTrainee[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const clearData = () => {
    setAdmins([]);
    setCandidates([]);
    setAvailableUsers([]);
  };

  return (
    <TeamDataContext.Provider
      value={{
        admins,
        candidates,
        availableUsers,
        isLoading,
        setAdmins,
        setCandidates,
        setAvailableUsers,
        setIsLoading,
        clearData,
      }}
    >
      {children}
    </TeamDataContext.Provider>
  );
};

export const useTeamData = () => {
  const context = useContext(TeamDataContext);
  if (!context) {
    throw new Error('useTeamData must be used within TeamDataProvider');
  }
  return context;
};

export type { TeamAdmin, TeamTrainee, AvailableUser };
